import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth/auth-context.js';
import { AppShell } from '../layout/app-shell.js';
import { SessionContext } from '../../lib/session/session-context.js';
import { SessionExpiredDialog } from './session-expired-dialog.js';

/**
 * The gate between the auth screens and the application.
 *
 * The `loading` branch is the one that matters. Cookie-authenticated apps cannot know whether
 * somebody is signed in until the server says so, and the classic bug is to treat "not yet
 * known" as "not signed in" — which flashes the login screen at every returning user for as
 * long as the round trip takes, and on a slow Indian 4G connection that is long enough to make
 * a shop think they have been logged out.
 */
function AuthSplash(): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <p role="status" className="text-sm text-muted-foreground">
        {t('common.loading')}
      </p>
    </div>
  );
}

/**
 * Wraps the private routes. An anonymous visitor is sent to the login screen with the address
 * they were trying to reach, so that after signing in they land where they meant to go rather
 * than on the dashboard — which matters most for the case it exists for: a link to one order,
 * opened from WhatsApp, on a phone whose session ended overnight.
 *
 * The bare root is no exception. The marketing landing lives on its own domain as a static
 * site (apps/landing), so the app host has nothing to show a stranger but the sign-in screen.
 */
export function RequireAuth({ children }: { children: ReactNode }): ReactElement {
  const auth = useAuth();
  const location = useLocation();

  if (auth.status === 'loading') return <AuthSplash />;
  if (auth.status === 'anonymous') {
    return (
      <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }
  return <>{children}</>;
}

/**
 * The mirror image: somebody already signed in has no business on the login screen.
 *
 * This is also what performs the post-sign-in navigation, and deliberately so. If the login
 * screen navigated for itself, its imperative `navigate()` and this component's redirect would
 * both fire on the same render — and whichever landed first decided where the person ended up.
 * Signing in only sets the session; where that lands is decided here, in one place, from the
 * address `RequireAuth` recorded on the way out.
 */
export function GuestOnly({ children }: { children: ReactNode }): ReactElement {
  const auth = useAuth();
  const location = useLocation();

  if (auth.status === 'loading') return <AuthSplash />;
  if (auth.status === 'authenticated') {
    const intended = (location.state as { from?: string } | null)?.from;
    return <Navigate to={intended?.startsWith('/') ? intended : '/'} replace />;
  }
  return <>{children}</>;
}

/**
 * The application shell, with the session plugged into it.
 *
 * This is where the two halves meet: the shell renders the shop name and the role from
 * `SessionContext`, and the auth state is what fills it. Signing out from the account menu
 * ends the session and lands on the login screen.
 *
 * The expiry dialog is rendered HERE, over the shell, rather than as a redirect. A session
 * that dies while a manager is halfway through booking an order must not take the order with
 * it — the screen stays mounted, the dialog asks for the password, and afterwards they are
 * looking at exactly what they were looking at before.
 */
export function AuthenticatedShell(): ReactElement {
  const auth = useAuth();
  const navigate = useNavigate();

  const session =
    auth.user && auth.role
      ? {
          shopName: auth.shop?.name ?? '',
          userName: auth.user.name,
          role: auth.role,
        }
      : null;

  return (
    <SessionContext.Provider value={session}>
      <AppShell
        onSignOut={() => {
          void (async () => {
            await auth.signOut();
            void navigate('/login', { replace: true });
          })();
        }}
      />
      {auth.expired ? <SessionExpiredDialog /> : null}
    </SessionContext.Provider>
  );
}
